import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Calendar, Clock, Users, MapPin, Edit2, Target, AlertTriangle, CheckCircle2, ShieldAlert, Loader2, Search, ChevronDown, ChevronUp, Phone, ArrowRightLeft, ExternalLink, Trash2, Archive, PauseCircle, PlayCircle } from "lucide-react";
import { Sidebar } from "@/components/Navigation";
import BottomNav from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import TimeInput from "@/components/TimeInput";
import CohortManagement from "@/pages/CohortManagement";
import { BatchChangeDialog } from "@/components/students/BatchChangeDialog";
import { BatchRetirementDialog } from "@/components/students/BatchRetirementDialog";
import { format } from "date-fns";
import api from "@/lib/api";
import { toast } from "sonner";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon";
import { ContactPickerDialog } from "@/components/shared/ContactPickerDialog";
import { openWhatsApp, buildContactOptions } from "@/lib/contactActions";
import { ResilientFetchBanner, runWithSettled } from "@/lib/resilientFetch";
import { fmt } from "@/lib/timezone";
import { useBrand } from "@/lib/brand";
import { parseDateOnly, serializeDateOnly, todayDateOnly, compareDateOnly } from "@/lib/dateOnly";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const ClassManagement = ({ user, onLogout }) => {
  const { default_timezone } = useBrand();
  const [batches, setBatches] = useState([]);
  const [styles, setStyles] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [masterInstructors, setMasterInstructors] = useState([]);
  const [studios, setStudios] = useState([]);
  const [tenures, setTenures] = useState([]);
  const [pricingData, setPricingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchFailures, setFetchFailures] = useState([]);
  const [retrying, setRetrying] = useState(false);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [cohortDialogBatch, setCohortDialogBatch] = useState(null);
  const [cohortDialogFocus, setCohortDialogFocus] = useState(null); // "interest" to scroll there
  const [searchParams, setSearchParams] = useSearchParams();
  const [conflictCheck, setConflictCheck] = useState(null); // {ok, conflicts_count, conflicts: []} | null
  const [checkingConflicts, setCheckingConflicts] = useState(false);
  
  const [formData, setFormData] = useState(() => ({
    name: "",
    style_id: "",
    level: "Beginner",
    instructor_id: "",
    instructor_name: "",
    studio_id: localStorage.getItem("active_studio_id") || "",
    room_id: "",
    schedule: [],
    capacity: "",
    color: "#7C3AED",
    frequency_per_week: 1,
    start_date: "",
    end_date: "",
    whatsapp_group_link: "",
    whatsapp_invite_link: "",
  }));
  
  const [scheduleDay, setScheduleDay] = useState("");
  const [scheduleStart, setScheduleStart] = useState("");
  const [scheduleEnd, setScheduleEnd] = useState("");

  // Search + per-batch student roster ----------------------------------
  const [search, setSearch] = useState("");
  const [expandedBatchIds, setExpandedBatchIds] = useState(() => new Set());
  const [batchStudents, setBatchStudents] = useState({}); // batch_id -> {loading, error, data:{students,total_active_count,truncated}}
  const navigate = useNavigate();

  // Change-Batch dialog state (mirrors StudentManagement) ---------------
  const [batchChangeDialogOpen, setBatchChangeDialogOpen] = useState(false);
  const [activeChangeEnrollment, setActiveChangeEnrollment] = useState(null);

  // Instructor-change scope modal (only shown on Edit when instructor changes)
  const [instructorScopeModal, setInstructorScopeModal] = useState({
    open: false,
    payload: null,
    oldName: "",
    newName: "",
  });
  const [newBatchId, setNewBatchId] = useState("");
  const [batchChangeWarning, setBatchChangeWarning] = useState(null);

  // Phase-B: batch retirement (deactivate / archive) dialog
  const [retirementDialog, setRetirementDialog] = useState({ open: false, action: null, batch: null });
  const [showArchived, setShowArchived] = useState(false);

  // Feb 2026 — Feature 5: WhatsApp invite picker for the enrolled-student row.
  // `invitePicker` shape: { batch, student } — opened when one of the rows in
  // the expandable students table has multiple valid contacts. When only one
  // valid contact exists, we skip the dialog and invoke openWhatsApp directly.
  const [invitePicker, setInvitePicker] = useState(null);
  const { default_country_code } = useBrand();

  // Click handler shared by every enrolled-students WhatsApp icon.
  const handleInviteClick = (batch, student) => {
    if (!batch?.whatsapp_invite_link) {
      toast.error("WhatsApp invite link not configured for this batch");
      return;
    }
    const options = buildContactOptions({
      studentName: student.student_name,
      studentPhone: student.student_phone,
      guardianName: student.parent_name || "Guardian",
      guardianPhone: student.parent_phone,
    });
    if (options.length === 0) {
      toast.error("Valid phone number not available");
      return;
    }
    if (options.length === 1) {
      const onlyKey = options[0].key;
      const phone = onlyKey === "student" ? student.student_phone : student.parent_phone;
      openWhatsApp(phone, buildInviteMessage(batch, student), { defaultCountryCode: default_country_code });
      return;
    }
    setInvitePicker({ batch, student });
  };

  // Shared invite message — identical regardless of which recipient is chosen.
  const buildInviteMessage = (batch, student) =>
`Hi,

${student.student_name} has been enrolled in the batch:

${batch.name}

Please join the WhatsApp group using the link below:

${batch.whatsapp_invite_link}

Thank you.`;

  const handleInvitePickerPick = (recipientKey) => {
    const { batch, student } = invitePicker || {};
    if (!batch || !student) return;
    const phone = recipientKey === "student" ? student.student_phone : student.parent_phone;
    openWhatsApp(phone, buildInviteMessage(batch, student), { defaultCountryCode: default_country_code });
  };

  // Feb 2026 — Feature 4: batch list WhatsApp group icon.
  const handleOpenGroup = (batch) => {
    if (!batch?.whatsapp_group_link) {
      toast.error("WhatsApp group link not configured for this batch");
      return;
    }
    const win = window.open(batch.whatsapp_group_link, "_blank", "noopener,noreferrer");
    if (!win) {
      toast.message("Pop-up was blocked", {
        description: "Tap to open the WhatsApp group.",
        action: { label: "Open", onClick: () => window.open(batch.whatsapp_group_link, "_blank", "noopener,noreferrer") },
      });
    }
  };

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const getPrice = (styleId, tenureId) => pricingData.find((p) => p.style_id === styleId && p.tenure_id === tenureId);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  // Auto-open cohort dialog when navigated with ?openCohortBatchId=...
  useEffect(() => {
    const targetBatchId = searchParams.get("openCohortBatchId");
    const focus = searchParams.get("focus");
    if (targetBatchId && batches.length > 0) {
      const match = batches.find((b) => b.batch_id === targetBatchId);
      if (match) {
        setCohortDialogBatch(match);
        setCohortDialogFocus(focus || null);
        // Clean URL so refresh doesn't re-open
        const next = new URLSearchParams(searchParams);
        next.delete("openCohortBatchId");
        next.delete("focus");
        setSearchParams(next, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batches]);

  // Auto-open Edit Batch dialog when navigated with ?editBatchId=... (e.g. from Studio Calendar)
  useEffect(() => {
    const editBatchId = searchParams.get("editBatchId");
    if (editBatchId && batches.length > 0) {
      const match = batches.find((b) => b.batch_id === editBatchId);
      if (match) {
        startEditBatch(match);
        // Clean URL so refresh doesn't re-open
        const next = new URLSearchParams(searchParams);
        next.delete("editBatchId");
        setSearchParams(next, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batches]);

  const buildFetchers = () => ({
    "batches":      () => api.get(`/batches${showArchived ? "?include_archived=true" : ""}`),
    "dance styles": () => api.get("/dance-styles"),
    "instructors":  () => api.get("/users?role=instructor"),
    "studios":      () => api.get("/studios"),
    "masters":      () => api.get("/instructors").catch(() => ({ data: { instructors: [] } })),
    "tenures":      () => api.get("/tenures"),
    "pricing":      () => api.get("/pricing"),
  });

  const applyResults = (results) => {
    if (results["batches"])      setBatches(results["batches"].data.batches);
    if (results["dance styles"]) setStyles(results["dance styles"].data.styles);
    if (results["instructors"])  setInstructors(results["instructors"].data.users);
    if (results["studios"])      setStudios(results["studios"].data.studios);
    if (results["masters"])      setMasterInstructors(results["masters"].data.instructors);
    if (results["tenures"])      setTenures(results["tenures"].data.tenures);
    if (results["pricing"])      setPricingData(results["pricing"].data.pricing);
  };

  const fetchData = async () => {
    const { results, failures } = await runWithSettled(buildFetchers());
    applyResults(results);
    setFetchFailures(failures);
    setLoading(false);
  };

  const retryFailed = async () => {
    if (!fetchFailures.length) return;
    setRetrying(true);
    const all = buildFetchers();
    const onlyFailed = Object.fromEntries(fetchFailures.map((f) => [f.label, all[f.label]]));
    const { results, failures } = await runWithSettled(onlyFailed);
    applyResults(results);
    setFetchFailures(failures);
    setRetrying(false);
    if (!failures.length) toast.success("Refreshed");
  };

  const addSchedule = () => {
    if (!scheduleDay || !scheduleStart || !scheduleEnd) {
      toast.error("Please fill all schedule fields");
      return;
    }
    const newSchedule = {
      day: scheduleDay,
      start_time: scheduleStart,
      end_time: scheduleEnd,
    };
    setFormData({ ...formData, schedule: [...formData.schedule, newSchedule] });
    setScheduleDay("");
    setScheduleStart("");
    setScheduleEnd("");
    setConflictCheck(null); // schedule changed → invalidate stale check
    toast.success("Schedule added");
  };

  const removeSchedule = (idx) => {
    setFormData({ ...formData, schedule: formData.schedule.filter((_, i) => i !== idx) });
    setConflictCheck(null);
  };

  const checkConflicts = async () => {
    if (!formData.studio_id || !formData.room_id || formData.schedule.length === 0) {
      toast.error("Pick a studio, room and add at least one schedule slot first");
      return;
    }
    setCheckingConflicts(true);
    try {
      const res = await api.post("/batches/check-schedule-conflicts", {
        studio_id: formData.studio_id,
        room_id: formData.room_id,
        schedule: formData.schedule,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        instructor_id: formData.instructor_id || null,
        exclude_batch_id: editMode && editingBatch ? editingBatch.batch_id : null,
      });
      setConflictCheck(res.data);
      if (res.data.ok && !(res.data.warnings_count > 0)) {
        toast.success("No conflicts in this room");
      } else if (!res.data.ok) {
        toast.warning(`${res.data.conflicts_count} conflict(s) found`);
      } else {
        toast.info(`${res.data.warnings_count} availability warning(s) — non-blocking`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to check conflicts");
    } finally {
      setCheckingConflicts(false);
    }
  };

  const createBatch = async () => {
    if (!formData.name || !formData.style_id || (!formData.instructor_id && !formData.instructor_name) || !formData.studio_id || !formData.room_id || !formData.capacity) {
      toast.error("Please fill all required fields");
      return;
    }

    if (formData.schedule.length === 0) {
      toast.error("Please add at least one schedule");
      return;
    }

    if (!formData.start_date) {
      toast.error("Batch start date is required");
      return;
    }
    if (formData.start_date && formData.end_date && compareDateOnly(formData.start_date, formData.end_date) > 0) {
      toast.error("Start date cannot be after end date");
      return;
    }

    try {
      const batchData = {
        ...formData,
        capacity: parseInt(formData.capacity),
        frequency_per_week: parseInt(formData.frequency_per_week),
        // Operational date-only fields — NEVER pass through `new Date()`.
        // Backend treats these as pure calendar dates (no timezone).
        start_date: serializeDateOnly(formData.start_date),
        end_date: serializeDateOnly(formData.end_date),
      };
      
      if (editMode && editingBatch) {
        // Detect instructor change — pause for scope modal
        const oldId = editingBatch.instructor_id || "";
        const oldName = editingBatch.instructor_name || "";
        const newId = formData.instructor_id || "";
        const newName = formData.instructor_name || "";
        const instructorChanged = (oldId || newId) ? oldId !== newId : oldName !== newName;
        if (instructorChanged) {
          setInstructorScopeModal({
            open: true,
            payload: batchData,
            oldName: oldName || "(none)",
            newName: newName || "(none)",
          });
          return; // Wait for user choice; saveBatchWithScope will call api.put
        }
        await api.put(`/batches/${editingBatch.batch_id}`, batchData);
        toast.success("Batch updated successfully!");
      } else {
        // Create new batch
        await api.post("/batches", batchData);
        toast.success("Batch created successfully!");
      }
      
      fetchData();
      setOpen(false);
      setEditMode(false);
      setEditingBatch(null);
      setConflictCheck(null);
      setFormData({
        name: "",
        style_id: "",
        level: "Beginner",
        instructor_id: "",
        studio_id: localStorage.getItem("active_studio_id") || "",
        room_id: "",
        schedule: [],
        capacity: "",
        color: "#7C3AED",
        frequency_per_week: 1,
        start_date: "",
        end_date: "",
        whatsapp_group_link: "",
        whatsapp_invite_link: "",
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save batch");
    }
  };

  // Persists a pending edit-batch update with the chosen instructor-scope.
  const submitBatchWithScope = async (scope) => {
    const { payload } = instructorScopeModal;
    if (!editingBatch || !payload) {
      setInstructorScopeModal({ open: false, payload: null, oldName: "", newName: "" });
      return;
    }
    try {
      const res = await api.put(
        `/batches/${editingBatch.batch_id}?instructor_change_scope=${scope}`,
        payload,
      );
      const cascaded = res?.data?.cascaded_session_count || 0;
      if (scope === "future_only" && cascaded > 0) {
        toast.success(`Batch updated. ${cascaded} future session(s) reassigned.`);
      } else if (scope === "future_only") {
        toast.success("Batch updated. No future sessions needed reassigning.");
      } else {
        toast.success("Batch updated. Existing sessions left untouched.");
      }
      setInstructorScopeModal({ open: false, payload: null, oldName: "", newName: "" });
      fetchData();
      setOpen(false);
      setEditMode(false);
      setEditingBatch(null);
      setConflictCheck(null);
      setFormData({
        name: "",
        style_id: "",
        level: "Beginner",
        instructor_id: "",
        instructor_name: "",
        studio_id: localStorage.getItem("active_studio_id") || "",
        room_id: "",
        schedule: [],
        capacity: "",
        color: "#7C3AED",
        frequency_per_week: 1,
        start_date: "",
        end_date: "",
        whatsapp_group_link: "",
        whatsapp_invite_link: "",
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save batch");
    }
  };

  const startEditBatch = (batch) => {
    setEditMode(true);
    setEditingBatch(batch);
    setFormData({
      name: batch.name,
      style_id: batch.style_id,
      level: batch.level,
      instructor_id: batch.instructor_id || "",
      instructor_name: batch.instructor_name || "",
      studio_id: batch.studio_id,
      room_id: batch.room_id,
      schedule: batch.schedule || [],
      capacity: (batch.capacity ?? 20).toString(),
      color: batch.color || "#7C3AED",
      frequency_per_week: batch.frequency_per_week || 1,
      // Pure calendar dates — never pass through new Date(). parseDateOnly
      // tolerates the legacy naive datetime wire format and the new
      // date-only string format equally.
      start_date: parseDateOnly(batch.start_date),
      end_date: parseDateOnly(batch.end_date),
      whatsapp_group_link: batch.whatsapp_group_link || "",
      whatsapp_invite_link: batch.whatsapp_invite_link || "",
    });
    setOpen(true);
  };

  // Delete-batch flow with proper cascade — admin only.
  // First call returns 409 if active enrollments exist; we surface a confirm prompt
  // and retry with ?force=true.
  const handleDeleteBatch = async (batch) => {
    if (!window.confirm(`Delete "${batch.name}"?\n\nThis will cancel ALL future scheduled sessions for this batch. Completed sessions will be preserved for history & payroll.\n\nThis action cannot be undone.`)) {
      return;
    }
    try {
      const res = await api.delete(`/batches/${batch.batch_id}`);
      toast.success(`Batch deleted — ${res.data.sessions_cancelled} session(s) cancelled, ${res.data.completed_sessions_preserved} completed preserved`);
      fetchBatches();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // 409 → active enrollments exist
      if (e.response?.status === 409 && detail?.active_enrollment_count) {
        const count = detail.active_enrollment_count;
        if (window.confirm(`This batch has ${count} active enrollment(s).\n\nDeleting will leave those students stranded — they should ideally be moved or discontinued first.\n\nProceed anyway?`)) {
          try {
            const res = await api.delete(`/batches/${batch.batch_id}?force=true`);
            toast.success(`Batch force-deleted — ${res.data.sessions_cancelled} session(s) cancelled`);
            fetchBatches();
          } catch (e2) {
            toast.error(e2.response?.data?.detail || "Failed to delete batch");
          }
        }
        return;
      }
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Failed to delete batch"));
    }
  };

  // Reactivate an inactive batch (Phase-A PATCH endpoint).
  const handleReactivate = async (batch) => {
    try {
      await api.patch(`/batches/${batch.batch_id}/status`, { status: "active" });
      toast.success("Batch reactivated");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reactivate");
    }
  };

  const selectedStudio = studios.find(s => s.studio_id === formData.studio_id);

  // ---------- Search filter ----------
  const filteredBatches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return batches;
    return batches.filter((batch) => {
      const studio = studios.find((s) => s.studio_id === batch.studio_id);
      const inst = instructors.find((i) => i.user_id === batch.instructor_id)
        || masterInstructors.find((i) => i.instructor_id === batch.instructor_id);
      const haystack = [
        batch.name,
        inst?.name,
        batch.instructor_name,
        studio?.name,
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [batches, studios, instructors, masterInstructors, search]);

  // ---------- Lazy-load enrolled students for a batch ----------
  const fetchBatchStudents = async (batchId) => {
    setBatchStudents((prev) => ({ ...prev, [batchId]: { loading: true } }));
    try {
      const res = await api.get(`/batches/${batchId}/enrolled-students`);
      setBatchStudents((prev) => ({ ...prev, [batchId]: { loading: false, data: res.data } }));
    } catch (e) {
      setBatchStudents((prev) => ({ ...prev, [batchId]: { loading: false, error: e.response?.data?.detail || "Failed to load students" } }));
    }
  };

  const toggleStudentsExpanded = (batchId) => {
    setExpandedBatchIds((prev) => {
      const next = new Set(prev);
      if (next.has(batchId)) {
        next.delete(batchId);
      } else {
        next.add(batchId);
        if (!batchStudents[batchId]?.data) fetchBatchStudents(batchId);
      }
      return next;
    });
  };

  // ---------- Change-Batch handlers (mirrors StudentManagement) ----------
  const openInlineBatchChange = (row, currentBatchId) => {
    // Build a synthetic enrollment object compatible with BatchChangeDialog props
    const currentBatch = batches.find((b) => b.batch_id === currentBatchId);
    setActiveChangeEnrollment({
      enrollment_id: row.enrollment_id,
      batch_id: currentBatchId,
      student_id: row.student_id,
      batch: currentBatch,
      pricing: { tenure_id: row.tenure_id || null },
    });
    setNewBatchId("");
    setBatchChangeWarning(null);
    setBatchChangeDialogOpen(true);
  };

  const checkBatchChange = (nextBatchId) => {
    setNewBatchId(nextBatchId);
    if (!activeChangeEnrollment || !nextBatchId) {
      setBatchChangeWarning(null);
      return;
    }
    const currentBatch = batches.find((b) => b.batch_id === activeChangeEnrollment.batch_id);
    const nextBatch = batches.find((b) => b.batch_id === nextBatchId);
    if (!currentBatch || !nextBatch) return;
    const warnings = [];
    if (currentBatch.style_id !== nextBatch.style_id) {
      const cs = styles.find((s) => s.style_id === currentBatch.style_id);
      const ns = styles.find((s) => s.style_id === nextBatch.style_id);
      warnings.push(`Dance style will change from "${cs?.name}" to "${ns?.name}"`);
    }
    const cp = getPrice(currentBatch.style_id, activeChangeEnrollment.pricing?.tenure_id);
    const np = getPrice(nextBatch.style_id, activeChangeEnrollment.pricing?.tenure_id);
    if (cp?.price !== np?.price) {
      warnings.push(`Pricing may differ for the new batch (Current: ₹${cp?.price ?? 'N/A'}, New: ₹${np?.price ?? 'N/A'})`);
    }
    setBatchChangeWarning(warnings.length > 0 ? warnings : null);
  };

  const confirmInlineBatchChange = async () => {
    if (!activeChangeEnrollment || !newBatchId) return;
    try {
      await api.put(`/enrollments/${activeChangeEnrollment.enrollment_id}`, { batch_id: newBatchId });
      toast.success("Batch changed successfully");
      setBatchChangeDialogOpen(false);
      // Refresh the source batch's roster (and also the destination if expanded)
      await fetchBatchStudents(activeChangeEnrollment.batch_id);
      if (expandedBatchIds.has(newBatchId)) await fetchBatchStudents(newBatchId);

      // Feb 2026 enhancement (Q2=b): when the destination batch has a
      // `whatsapp_invite_link`, auto-open the recipient picker. The source-
      // batch student row already exposes name + phone + parent_phone in the
      // enriched payload, so no extra fetch is needed.
      const destinationBatch = batches.find((b) => b.batch_id === newBatchId);
      if (destinationBatch?.whatsapp_invite_link) {
        const studentRow = {
          // ContactPickerDialog + invite template expect `name` + `phone` +
          // `parent_phone` shape (consistent with the student doc). Map from
          // the enriched enrollment row format used here.
          name: activeChangeEnrollment.student_name,
          phone: activeChangeEnrollment.student_phone,
          parent_name: activeChangeEnrollment.parent_name,
          parent_phone: activeChangeEnrollment.parent_phone,
        };
        const opts = buildContactOptions({
          studentName: studentRow.name,
          studentPhone: studentRow.phone,
          guardianName: studentRow.parent_name || "Guardian",
          guardianPhone: studentRow.parent_phone,
        });
        if (opts.length > 0) {
          setInvitePicker({ batch: destinationBatch, student: { ...studentRow, student_name: studentRow.name, student_phone: studentRow.phone } });
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change batch");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 md:pl-64">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="p-4 md:p-8 pb-20 md:pb-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold font-heading text-slate-900" data-testid="class-management-title">
              Class Management
            </h1>
            <p className="text-sm text-slate-600 mt-1">Manage dance classes and batches</p>
          </div>
          <Dialog open={open} onOpenChange={(isOpen) => {
            setOpen(isOpen);
            if (!isOpen) {
              setEditMode(false);
              setEditingBatch(null);
              setFormData({
                name: "",
                style_id: "",
                level: "Beginner",
                instructor_id: "",
                instructor_name: "",
                studio_id: localStorage.getItem("active_studio_id") || "",
                room_id: "",
                schedule: [],
                capacity: "",
                color: "#7C3AED",
                frequency_per_week: 1,
                start_date: "",
                end_date: "",
                whatsapp_group_link: "",
                whatsapp_invite_link: "",
              });
              setScheduleDay("");
              setScheduleStart("");
              setScheduleEnd("");
            }
          }}>
            <DialogTrigger asChild>
              <Button data-testid="create-batch-button" className="h-12 rounded-full bg-primary hover:bg-primary/90 font-medium">
                <Plus className="w-5 h-5 mr-2" />
                Create Batch
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh]">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">
                  {editMode ? "Edit Batch" : "Create New Batch"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-6 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="batch-name">Batch Name</Label>
                    <Input
                      id="batch-name"
                      data-testid="batch-name-input"
                      placeholder="e.g., Hip Hop Beginner Morning"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="h-12 rounded-xl"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="batch-style">Dance Style</Label>
                    <select
                      id="batch-style"
                      data-testid="batch-style-select"
                      value={formData.style_id}
                      onChange={(e) => setFormData({ ...formData, style_id: e.target.value })}
                      className="w-full h-12 rounded-xl border border-slate-200 px-4 bg-white"
                    >
                      <option value="">Select Style</option>
                      {styles.map((style) => (
                        <option key={style.style_id} value={style.style_id}>
                          {style.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="batch-level">Level</Label>
                    <select
                      id="batch-level"
                      data-testid="batch-level-select"
                      value={formData.level}
                      onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                      className="w-full h-12 rounded-xl border border-slate-200 px-4 bg-white"
                    >
                      <option value="Beginner">Beginner</option>
                      <option value="Intermediate">Intermediate</option>
                      <option value="Advanced">Advanced</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="batch-instructor">Instructor</Label>
                    <select
                      id="batch-instructor"
                      data-testid="batch-instructor-select"
                      value={formData.instructor_id}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "__free_text__") {
                          setFormData({ ...formData, instructor_id: "", instructor_name: "" });
                        } else {
                          // Phase 1: /api/instructors is the unified source (admins + instructors).
                          const hit = masterInstructors.find(i => (i.instructor_id || i.user_id) === val);
                          setFormData({ ...formData, instructor_id: val, instructor_name: hit?.name || "" });
                        }
                      }}
                      className="w-full h-12 rounded-xl border border-slate-200 px-4 bg-white"
                    >
                      <option value="">Select Instructor</option>
                      {masterInstructors.map((inst) => (
                        <option key={inst.instructor_id || inst.user_id} value={inst.instructor_id || inst.user_id}>
                          {inst.name}{inst.role === "admin" ? "  •  Admin" : ""}
                        </option>
                      ))}
                      <option value="__free_text__">-- Type a name --</option>
                    </select>
                    {formData.instructor_id === "" && (
                      <Input
                        data-testid="batch-instructor-freetext"
                        value={formData.instructor_name}
                        onChange={(e) => setFormData({ ...formData, instructor_name: e.target.value })}
                        placeholder="Type instructor name"
                        className="h-11 rounded-xl mt-2"
                      />
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="batch-capacity">Capacity</Label>
                    <Input
                      id="batch-capacity"
                      data-testid="batch-capacity-input"
                      type="number"
                      placeholder="Max students"
                      value={formData.capacity}
                      onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                      className="h-12 rounded-xl"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="batch-color">Class Color (for easy identification)</Label>
                    <div className="flex gap-3 items-center">
                      <Input
                        id="batch-color"
                        data-testid="batch-color-input"
                        type="color"
                        value={formData.color}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        className="h-12 w-24 rounded-xl cursor-pointer"
                      />
                      <span className="text-sm text-slate-600">Selected: {formData.color}</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="batch-frequency">Frequency (Classes per Week)</Label>
                    <Input
                      id="batch-frequency"
                      data-testid="batch-frequency-input"
                      type="number"
                      min="1"
                      max="7"
                      placeholder="e.g., 2"
                      value={formData.frequency_per_week}
                      onChange={(e) => setFormData({ ...formData, frequency_per_week: e.target.value })}
                      className="h-12 rounded-xl"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="batch-start-date">Batch Start Date <span className="text-red-500">*</span></Label>
                    <Input
                      id="batch-start-date"
                      data-testid="batch-start-date-input"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="h-12 rounded-xl"
                    />
                    <p className="text-xs text-slate-500">When this batch begins.</p>
                    {formData.start_date && compareDateOnly(formData.start_date, todayDateOnly()) > 0 && (
                      <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        📅 This batch starts in the future — it will only appear on the calendar from <strong>{fmt(formData.start_date, "dd MMM yyyy", default_timezone)}</strong> onwards.
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="batch-end-date">Batch End Date (Optional)</Label>
                    <Input
                      id="batch-end-date"
                      data-testid="batch-end-date-input"
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="h-12 rounded-xl"
                    />
                    <p className="text-xs text-slate-500">Leave empty for ongoing classes. Won't show on calendar after this date.</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="batch-studio">Studio Location</Label>
                    <select
                      id="batch-studio"
                      data-testid="batch-studio-select"
                      value={formData.studio_id}
                      onChange={(e) => { setFormData({ ...formData, studio_id: e.target.value, room_id: "" }); setConflictCheck(null); }}
                      className="w-full h-12 rounded-xl border border-slate-200 px-4 bg-white"
                    >
                      <option value="">Select Studio</option>
                      {studios.filter(s => s.is_active !== false).map((studio) => (
                        <option key={studio.studio_id} value={studio.studio_id}>
                          {studio.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="batch-room">Room</Label>
                    <select
                      id="batch-room"
                      data-testid="batch-room-select"
                      value={formData.room_id}
                      onChange={(e) => { setFormData({ ...formData, room_id: e.target.value }); setConflictCheck(null); }}
                      className="w-full h-12 rounded-xl border border-slate-200 px-4 bg-white"
                      disabled={!formData.studio_id}
                    >
                      <option value="">Select Room</option>
                      {selectedStudio?.rooms.filter(r => r.is_active !== false).map((room) => (
                        <option key={room.room_id} value={room.room_id}>
                          {room.name} (Capacity: {room.capacity})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <Label className="text-lg font-heading">Schedule</Label>
                  <div className="space-y-2 mt-3">
                    <select
                      data-testid="schedule-day-select"
                      value={scheduleDay}
                      onChange={(e) => setScheduleDay(e.target.value)}
                      className="w-full h-10 rounded-lg border border-slate-200 px-3 bg-white"
                    >
                      <option value="">Select Day</option>
                      <option value="Monday">Monday</option>
                      <option value="Tuesday">Tuesday</option>
                      <option value="Wednesday">Wednesday</option>
                      <option value="Thursday">Thursday</option>
                      <option value="Friday">Friday</option>
                      <option value="Saturday">Saturday</option>
                      <option value="Sunday">Sunday</option>
                    </select>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <Label className="text-xs text-slate-500 mb-1 block">Start</Label>
                        <TimeInput
                          data-testid="schedule-start-input"
                          value={scheduleStart}
                          onChange={(e) => setScheduleStart(e.target.value)}
                        />
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs text-slate-500 mb-1 block">End</Label>
                        <TimeInput
                          data-testid="schedule-end-input"
                          value={scheduleEnd}
                          onChange={(e) => setScheduleEnd(e.target.value)}
                        />
                      </div>
                      <div className="pt-4">
                        <Button data-testid="add-schedule-button" onClick={addSchedule} size="sm" className="h-10 rounded-lg">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {formData.schedule.length > 0 && (
                    <div className="space-y-2 mt-4">
                      {formData.schedule.map((sch, index) => (
                        <div
                          key={`sch-slot-${index}`}
                          data-testid={`schedule-item-${index}`}
                          className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg text-sm"
                        >
                          <Calendar className="w-4 h-4 text-primary" />
                          <span className="font-medium w-24">{sch.day}</span>
                          <Clock className="w-4 h-4 text-slate-400" />
                          {/* Feb 2026 — inline time edit. Day-of-week stays
                              read-only (backend blocks day-set changes by
                              design; use Remove + Add to restructure days). */}
                          <input
                            type="time"
                            value={sch.start_time}
                            data-testid={`schedule-start-${index}`}
                            onChange={(e) => {
                              const next = [...formData.schedule];
                              next[index] = { ...next[index], start_time: e.target.value };
                              setFormData({ ...formData, schedule: next });
                            }}
                            className="h-8 px-2 rounded-md border border-slate-200 bg-white text-sm w-24 sm:w-28"
                          />
                          <span className="text-slate-400 text-xs">to</span>
                          <input
                            type="time"
                            value={sch.end_time}
                            data-testid={`schedule-end-${index}`}
                            onChange={(e) => {
                              const next = [...formData.schedule];
                              next[index] = { ...next[index], end_time: e.target.value };
                              setFormData({ ...formData, schedule: next });
                            }}
                            className="h-8 px-2 rounded-md border border-slate-200 bg-white text-sm w-24 sm:w-28"
                          />
                          <div className="flex-1"></div>
                          <button
                            type="button"
                            onClick={() => removeSchedule(index)}
                            className="text-slate-400 hover:text-red-500 text-xs font-medium px-2"
                            data-testid={`remove-schedule-${index}`}
                            title="Remove this schedule slot (Note: batch day-set cannot be changed once created — use this only to drop a day and re-add it with a different day)"
                          >
                            Remove
                          </button>
                        </div>
                      ))}

                      {/* Check Conflicts Button */}
                      <Button
                        type="button"
                        data-testid="check-conflicts-button"
                        onClick={checkConflicts}
                        disabled={checkingConflicts || !formData.studio_id || !formData.room_id}
                        variant="outline"
                        className="w-full h-10 rounded-lg mt-2"
                      >
                        {checkingConflicts ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking…</>
                        ) : (
                          <><ShieldAlert className="w-4 h-4 mr-2" /> Check Schedule Conflicts</>
                        )}
                      </Button>

                      {/* Conflict Check Results */}
                      {conflictCheck && conflictCheck.ok && (conflictCheck.warnings_count || 0) === 0 && (
                        <div data-testid="conflict-check-ok" className="flex items-start gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <p className="text-emerald-800">
                            No conflicts found in this room for the scheduled slots.
                          </p>
                        </div>
                      )}

                      {conflictCheck && !conflictCheck.ok && (
                        <div data-testid="conflict-check-warn" className="rounded-xl bg-red-50 border border-red-200 p-3 space-y-2 text-sm">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="font-semibold text-red-800">
                                {conflictCheck.conflicts_count} conflict{conflictCheck.conflicts_count > 1 ? "s" : ""} detected
                              </p>
                              <p className="text-xs text-red-700 mt-0.5">
                                You can still save — the warning will be visible. Existing classes/rentals are not affected.
                              </p>
                            </div>
                          </div>
                          <ul className="space-y-1.5 pl-6 list-disc">
                            {conflictCheck.conflicts.map((c, idx) => (
                              <li key={`${c.day || ""}-${c.slot_start || ""}-${c.slot_end || ""}-${c.with || idx}`} data-testid={`conflict-item-${idx}`} className="text-red-800 text-xs">
                                <span className="font-medium">{c.day}</span> {c.slot_start}–{c.slot_end} clashes with{" "}
                                {c.type === "class" ? (
                                  <span className="font-medium">class "{c.with}" ({c.with_time})</span>
                                ) : (
                                  <span className="font-medium">
                                    rental "{c.with}" ({c.with_time}
                                    {c.date ? ` on ${fmt(c.date, "dd MMM", default_timezone)}` : ""})
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Instructor availability warnings (3c-ii: warn, don't block) */}
                      {conflictCheck && (conflictCheck.warnings_count || 0) > 0 && (
                        <div data-testid="conflict-check-instructor-warn" className="rounded-xl bg-amber-50 border border-amber-200 p-3 space-y-2 text-sm">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="font-semibold text-amber-800">
                                Instructor availability warning ({conflictCheck.warnings_count})
                              </p>
                              <p className="text-xs text-amber-700 mt-0.5">
                                You can still save — this is a soft warning (e.g. substitute teaching).
                              </p>
                            </div>
                          </div>
                          <ul className="space-y-1.5 pl-6 list-disc">
                            {conflictCheck.warnings.map((w, idx) => (
                              <li key={`w-${w.day || ""}-${w.slot_start || ""}-${w.with_id || idx}`} className="text-amber-800 text-xs">
                                <span className="font-medium">{w.day}</span> {w.slot_start}–{w.slot_end}: {w.with}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Feb 2026 — Feature 3: Communication Settings (WhatsApp). */}
                <div className="space-y-3 border-t border-slate-200 pt-4">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800">Communication Settings</h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Optional. Used by the WhatsApp icon on the batch list and the enrolled-students invite flow.
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs">WhatsApp Group Link</Label>
                    <Input
                      data-testid="batch-whatsapp-group-link"
                      value={formData.whatsapp_group_link}
                      onChange={(e) => setFormData({ ...formData, whatsapp_group_link: e.target.value.trim() })}
                      placeholder="https://chat.whatsapp.com/..."
                      className="h-10 rounded-lg"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">Opens the class group when the WhatsApp icon is clicked on the batch list.</p>
                  </div>
                  <div>
                    <Label className="text-xs">WhatsApp Invite Link</Label>
                    <Input
                      data-testid="batch-whatsapp-invite-link"
                      value={formData.whatsapp_invite_link}
                      onChange={(e) => setFormData({ ...formData, whatsapp_invite_link: e.target.value.trim() })}
                      placeholder="https://chat.whatsapp.com/..."
                      className="h-10 rounded-lg"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">Shared with enrolled students from the WhatsApp invite flow.</p>
                  </div>
                </div>

                <Button
                  data-testid="submit-batch-button"
                  onClick={createBatch}
                  className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 font-medium"
                >
                  {editMode ? "Update Batch" : "Create Batch"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {fetchFailures.length > 0 && (
          <ResilientFetchBanner
            failedLabels={fetchFailures.map((f) => f.label)}
            onRetry={retryFailed}
            retrying={retrying}
            className="mb-4"
            testid="classes-fetch-error"
          />
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : batches.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-slate-100">
            <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">{fetchFailures.some((f) => f.label === "batches") ? "Couldn't load classes. Tap Retry above." : "No batches yet. Create your first batch!"}</p>
          </div>
        ) : (
          <>
            {/* Search bar */}
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="class-search-input"
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by class name, instructor, or studio…"
                  className="pl-10 h-11 rounded-xl bg-white"
                />
              </div>
              <p className="text-xs text-slate-500 sm:ml-3">
                Showing {filteredBatches.length} of {batches.length} {batches.length === 1 ? "class" : "classes"}
              </p>
              <button
                type="button"
                data-testid="show-archived-toggle"
                onClick={() => setShowArchived((v) => !v)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${showArchived ? "border-slate-700 bg-slate-700 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`}
              >
                {showArchived ? "Hide Archived" : "Show Archived"}
              </button>
            </div>

            {filteredBatches.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-slate-100">
                <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">No classes match "<span className="font-medium">{search}</span>"</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {filteredBatches.map((batch, index) => {
                  const style = styles.find(s => s.style_id === batch.style_id);
                  const instructor = instructors.find(i => i.user_id === batch.instructor_id)
                    || masterInstructors.find(i => i.instructor_id === batch.instructor_id);
                  const studio = studios.find(s => s.studio_id === batch.studio_id);
              
              return (
                <motion.div
                  key={batch.batch_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-lg transition-all"
                  data-testid={`batch-card-${index}`}
                  style={{ borderLeft: `6px solid ${batch.color || '#7C3AED'}` }}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div 
                      className="p-3 rounded-xl"
                      style={{ backgroundColor: `${batch.color || '#7C3AED'}20` }}
                    >
                      <Calendar className="w-6 h-6" style={{ color: batch.color || '#7C3AED' }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold font-heading text-slate-900">{batch.name}</h3>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-xs font-medium">
                          {style?.name || 'Unknown Style'}
                        </span>
                        <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-xs font-medium">
                          {batch.level}
                        </span>
                        {/* Phase-B: status badge */}
                        {(batch.status || "active") === "inactive" && (
                          <span data-testid={`batch-status-${index}`} className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium inline-flex items-center gap-1">
                            <PauseCircle className="w-3 h-3" /> Inactive
                          </span>
                        )}
                        {(batch.status || "active") === "archived" && (
                          <span data-testid={`batch-status-${index}`} className="px-3 py-1 bg-slate-200 text-slate-600 rounded-full text-xs font-medium inline-flex items-center gap-1">
                            <Archive className="w-3 h-3" /> Archived
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Users className="w-4 h-4" />
                      <span>Instructor: {instructor?.name || 'Unknown'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <MapPin className="w-4 h-4" />
                      <span>{studio?.name || 'Unknown Studio'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <Users className="w-4 h-4" />
                      <span>Capacity: {batch.capacity}</span>
                    </div>
                    {batch.frequency_per_week && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Calendar className="w-4 h-4" />
                        <span>{batch.frequency_per_week}x per week</span>
                      </div>
                    )}
                    {batch.start_date && (
                      <div className="flex items-center gap-2 text-emerald-700">
                        <Calendar className="w-4 h-4" />
                        <span>Started: {fmt(batch.start_date, "dd MMM yyyy", default_timezone)}</span>
                      </div>
                    )}
                    {batch.end_date && (
                      <div className="flex items-center gap-2 text-orange-600">
                        <Clock className="w-4 h-4" />
                        <span>Ends: {fmt(batch.end_date, "dd MMM yyyy", default_timezone)}</span>
                      </div>
                    )}
                  </div>

                  {batch.schedule.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-xs font-medium text-slate-900 mb-2">Schedule:</p>
                      <div className="space-y-1">
                        {batch.schedule.map((sch, idx) => (
                          <div key={`${sch.day}-${sch.start_time}-${sch.end_time}-${idx}`} className="flex items-center gap-2 text-xs text-slate-600">
                            <span className="font-medium">{sch.day}:</span>
                            <span>{sch.start_time} - {sch.end_time}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 mt-4">
                    {(batch.status || "active") !== "archived" && (
                      <Button
                        data-testid={`edit-batch-${index}`}
                        onClick={() => startEditBatch(batch)}
                        className="flex-1 min-w-[110px] h-10 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700"
                      >
                        <Edit2 className="w-4 h-4 mr-2" />
                        Edit Batch
                      </Button>
                    )}
                    {/* Feb 2026 — Feature 4: open the WhatsApp class group. */}
                    {(batch.status || "active") !== "archived" && (
                      <Button
                        data-testid={`batch-whatsapp-${index}`}
                        onClick={() => handleOpenGroup(batch)}
                        className="h-10 px-3 rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200"
                        title={batch.whatsapp_group_link ? "Open WhatsApp class group" : "WhatsApp group link not configured for this batch"}
                      >
                        <WhatsAppIcon size={16} />
                      </Button>
                    )}
                    {(batch.status || "active") === "active" && (
                      <Button
                        data-testid={`manage-cohort-${index}`}
                        onClick={() => setCohortDialogBatch(batch)}
                        className="flex-1 min-w-[110px] h-10 rounded-full bg-violet-100 hover:bg-violet-200 text-violet-700"
                      >
                        <Target className="w-4 h-4 mr-2" />
                        Cohorts
                      </Button>
                    )}
                    {/* Phase-B: lifecycle actions (admin only) */}
                    {isAdmin && (batch.status || "active") === "active" && (
                      <Button
                        data-testid={`deactivate-batch-${index}`}
                        onClick={() => setRetirementDialog({ open: true, action: "deactivate", batch })}
                        className="h-10 px-3 rounded-full bg-amber-50 hover:bg-amber-100 text-amber-700"
                        title="Deactivate (pause) batch"
                      >
                        <PauseCircle className="w-4 h-4" />
                      </Button>
                    )}
                    {isAdmin && (batch.status || "active") === "inactive" && (
                      <Button
                        data-testid={`reactivate-batch-${index}`}
                        onClick={() => handleReactivate(batch)}
                        className="h-10 px-3 rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-700"
                        title="Reactivate batch"
                      >
                        <PlayCircle className="w-4 h-4" />
                      </Button>
                    )}
                    {isAdmin && (batch.status || "active") !== "archived" && (
                      <Button
                        data-testid={`archive-batch-${index}`}
                        onClick={() => setRetirementDialog({ open: true, action: "archive", batch })}
                        className="h-10 px-3 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700"
                        title="Archive (retire) batch — cannot be reversed"
                      >
                        <Archive className="w-4 h-4" />
                      </Button>
                    )}
                    {isAdmin && (batch.status || "active") !== "archived" && (
                      <Button
                        data-testid={`delete-batch-${index}`}
                        onClick={() => handleDeleteBatch(batch)}
                        className="h-10 px-3 rounded-full bg-red-50 hover:bg-red-100 text-red-700"
                        title="Delete batch (cascade-cancels future sessions)"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  {/* Enrolled Students — expandable */}
                  <div className="mt-4 pt-4 border-t">
                    <button
                      data-testid={`toggle-students-${index}`}
                      onClick={() => toggleStudentsExpanded(batch.batch_id)}
                      className="w-full flex items-center justify-between text-sm font-medium text-slate-700 hover:text-violet-700 transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Enrolled Students
                        {(() => {
                          // Prefer the live count when the row is expanded (it reflects any
                          // status changes that happened since the batches list was fetched).
                          // Otherwise fall back to the batch-list count from /batches so the
                          // number is visible without expanding.
                          const liveCount = batchStudents[batch.batch_id]?.data?.total_active_count;
                          const count = liveCount !== undefined ? liveCount : batch.enrolled_count;
                          if (count === undefined) return null;
                          return (
                            <span
                              data-testid={`enrolled-count-${index}`}
                              className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full text-xs font-medium"
                            >
                              {count}
                            </span>
                          );
                        })()}
                      </span>
                      {expandedBatchIds.has(batch.batch_id)
                        ? <ChevronUp className="w-4 h-4" />
                        : <ChevronDown className="w-4 h-4" />}
                    </button>

                    {expandedBatchIds.has(batch.batch_id) && (
                      <div className="mt-3 space-y-2">
                        {batchStudents[batch.batch_id]?.loading && (
                          <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Loading students…
                          </div>
                        )}
                        {batchStudents[batch.batch_id]?.error && (
                          <p className="text-sm text-red-600 py-2">{batchStudents[batch.batch_id].error}</p>
                        )}
                        {batchStudents[batch.batch_id]?.data && batchStudents[batch.batch_id].data.students.length === 0 && (
                          <p className="text-sm text-slate-500 py-2 text-center">No active students in this batch yet.</p>
                        )}
                        {batchStudents[batch.batch_id]?.data?.students?.map((row) => {
                          const renewalDate = row.renewal_date ? new Date(row.renewal_date) : null;
                          const outstanding = row.outstanding || 0;
                          return (
                            <div
                              key={row.enrollment_id}
                              data-testid={`enrolled-row-${row.enrollment_id}`}
                              className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                            >
                              <div className="min-w-0 flex-1">
                                <button
                                  onClick={() => navigate(`/students?student_id=${row.student_id}`)}
                                  className="text-sm font-medium text-slate-900 hover:text-violet-700 inline-flex items-center gap-1 truncate"
                                  data-testid={`open-profile-${row.student_id}`}
                                >
                                  {row.student_name}
                                  {/* Feb 2026 — Feature 5: append "(NY)" when age is a positive integer. */}
                                  {Number.isInteger(row.student_age) && row.student_age > 0 && (
                                    <span className="text-slate-500 font-normal ml-0.5">({row.student_age}Y)</span>
                                  )}
                                  <ExternalLink className="w-3 h-3 opacity-60" />
                                </button>
                                <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500 flex-wrap">
                                  {row.student_phone && (
                                    <a href={`tel:${row.student_phone}`} className="inline-flex items-center gap-1 hover:text-slate-700">
                                      <Phone className="w-3 h-3" /> {row.student_phone}
                                    </a>
                                  )}
                                  {renewalDate && (
                                    <span className={row.is_overdue_renewal ? "text-red-600 font-medium" : ""}>
                                      Renews: {format(renewalDate, "dd MMM yyyy")}
                                    </span>
                                  )}
                                  {row.created_by_name && (
                                    <span
                                      data-testid={`enrolled-created-by-${row.enrollment_id}`}
                                      className={`text-[11px] ${row.created_by_status === "legacy" ? "text-slate-400 italic" : row.created_by_status === "inactive" ? "text-amber-600" : "text-slate-500"}`}
                                      title={row.created_at ? `Enrolled on ${new Date(row.created_at).toLocaleString()}` : ""}
                                    >
                                      Enrolled by {row.created_by_name}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {outstanding > 0 ? (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 whitespace-nowrap">
                                    ₹{outstanding.toLocaleString()} Pending
                                  </span>
                                ) : (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-200 text-slate-600 whitespace-nowrap">
                                    ₹0
                                  </span>
                                )}
                                {/* Feb 2026 — Feature 5: WhatsApp invite icon. */}
                                <button
                                  onClick={() => handleInviteClick(batch, row)}
                                  data-testid={`enrolled-whatsapp-${row.enrollment_id}`}
                                  title={batch.whatsapp_invite_link ? "Send WhatsApp invite" : "WhatsApp invite link not configured for this batch"}
                                  className="inline-flex items-center justify-center w-8 h-8 rounded-full text-emerald-600 hover:bg-emerald-100 transition"
                                >
                                  <WhatsAppIcon size={16} />
                                </button>
                                {isAdmin && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    title="Change Batch"
                                    onClick={() => openInlineBatchChange(row, batch.batch_id)}
                                    data-testid={`change-batch-${row.enrollment_id}`}
                                    className="h-8 px-2"
                                  >
                                    <ArrowRightLeft className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {batchStudents[batch.batch_id]?.data?.truncated && (
                          <button
                            onClick={() => navigate("/students")}
                            className="w-full text-xs text-violet-700 hover:underline py-1 mt-1"
                          >
                            + {batchStudents[batch.batch_id].data.total_active_count - 100} more — open Student Management
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
              </div>
            )}
          </>
        )}
      </div>

      <BottomNav user={user} onLogout={onLogout} />

      {/* Feb 2026 — Feature 5: shared invite recipient picker. */}
      <ContactPickerDialog
        open={!!invitePicker}
        onOpenChange={(o) => { if (!o) setInvitePicker(null); }}
        title="Who would you like to message?"
        options={invitePicker ? buildContactOptions({
          studentName: invitePicker.student.student_name,
          studentPhone: invitePicker.student.student_phone,
          guardianName: invitePicker.student.parent_name || "Guardian",
          guardianPhone: invitePicker.student.parent_phone,
        }) : []}
        onPick={handleInvitePickerPick}
      />

      {/* Change Batch Dialog (mounted at page level, reused across batch cards) */}
      <BatchChangeDialog
        open={batchChangeDialogOpen}
        onOpenChange={(v) => {
          setBatchChangeDialogOpen(v);
          if (!v) {
            setActiveChangeEnrollment(null);
            setNewBatchId("");
            setBatchChangeWarning(null);
          }
        }}
        selectedEnrollment={activeChangeEnrollment}
        batches={batches}
        styles={styles}
        studios={studios}
        instructors={instructors}
        masterInstructors={masterInstructors}
        newBatchId={newBatchId}
        onCheckBatchChange={checkBatchChange}
        onConfirm={confirmInlineBatchChange}
        batchChangeWarning={batchChangeWarning}
      />

      {/* Phase-B: Batch Retirement Dialog (deactivate / archive with reassignment wizard) */}
      <BatchRetirementDialog
        open={retirementDialog.open}
        onOpenChange={(v) => setRetirementDialog((prev) => ({ ...prev, open: v }))}
        action={retirementDialog.action}
        batch={retirementDialog.batch}
        batches={batches}
        styles={styles}
        studios={studios}
        instructors={instructors}
        masterInstructors={masterInstructors}
        onCompleted={fetchData}
      />

      {/* Cohort Management Dialog */}
      <Dialog open={!!cohortDialogBatch} onOpenChange={(v) => { if (!v) { setCohortDialogBatch(null); setCohortDialogFocus(null); } }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh]">
          {cohortDialogBatch && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">{cohortDialogBatch.name} — Cohorts</DialogTitle>
              </DialogHeader>
              <div className="overflow-y-auto max-h-[70vh]">
                <CohortManagement
                  batch={cohortDialogBatch}
                  tenures={tenures}
                  pricing={pricingData}
                  styles={styles}
                  focus={cohortDialogFocus}
                  onClose={() => { setCohortDialogBatch(null); setCohortDialogFocus(null); }}
                />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Instructor change scope modal — shown when instructor field changed during edit */}
      <Dialog
        open={instructorScopeModal.open}
        onOpenChange={(v) => { if (!v) setInstructorScopeModal({ open: false, payload: null, oldName: "", newName: "" }); }}
      >
        <DialogContent className="sm:max-w-md" data-testid="instructor-scope-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-amber-600" /> Apply Instructor Change?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
              You are changing instructor from <strong>{instructorScopeModal.oldName}</strong> to <strong>{instructorScopeModal.newName}</strong>.
            </div>
            <p className="text-xs text-slate-600">
              Choose how to apply this change. <strong>Completed sessions and confirmed payroll are never modified.</strong>
            </p>
            <div className="space-y-2">
              <button
                onClick={() => submitBatchWithScope("future_only")}
                className="w-full text-left rounded-xl border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors px-4 py-3"
                data-testid="scope-future-only-btn"
              >
                <p className="font-semibold text-emerald-900 text-sm">Apply to all future sessions</p>
                <p className="text-xs text-emerald-700 mt-0.5">Reassigns every un-completed session of this batch from today onwards.</p>
              </button>
              <button
                onClick={() => submitBatchWithScope("none")}
                className="w-full text-left rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors px-4 py-3"
                data-testid="scope-none-btn"
              >
                <p className="font-semibold text-slate-900 text-sm">Don't touch existing sessions</p>
                <p className="text-xs text-slate-600 mt-0.5">Only the batch default is updated. Existing scheduled sessions keep their current instructor (use Calendar &rarr; substitute for one-offs).</p>
              </button>
            </div>
            <div className="flex justify-end pt-2">
              <Button
                variant="ghost"
                onClick={() => setInstructorScopeModal({ open: false, payload: null, oldName: "", newName: "" })}
                className="text-slate-500"
                data-testid="scope-cancel-btn"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClassManagement;